// Runtime enrichment for article HTML content.
// Adds: reading time, table of contents, FAQ structured data, anchor IDs on H2s.
// Designed to work automatically for ALL articles (current and future) without
// editing per-article data — derives everything from the HTML content.

export type TocItem = { id: string; text: string };
export type FaqItem = { question: string; answer: string };

function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80) || "section";
}

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
}

export function computeReadingMinutes(html: string): number {
  const words = stripTags(html).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

/**
 * Adds id="..." to every <h2> and returns the TOC list.
 * Skips the FAQ heading and any heading starting with "Pogosta vprašanja".
 */
export function enrichHeadings(html: string): { html: string; toc: TocItem[] } {
  const toc: TocItem[] = [];
  const used = new Set<string>();
  const out = html.replace(/<h2([^>]*)>([\s\S]*?)<\/h2>/gi, (_m, attrs: string, inner: string) => {
    const text = stripTags(inner);
    const isFaq = /pogosta\s+vpra[sš]anja|faq/i.test(text);
    if (isFaq) return `<h2${attrs}>${inner}</h2>`;
    let id = slugifyHeading(text);
    let i = 2;
    while (used.has(id)) id = `${slugifyHeading(text)}-${i++}`;
    used.add(id);
    toc.push({ id, text });
    if (/\sid=/.test(attrs)) return `<h2${attrs}>${inner}</h2>`;
    return `<h2${attrs} id="${id}">${inner}</h2>`;
  });
  return { html: enrichImages(out), toc };
}

/**
 * Enriches inline <img> tags inside article content:
 * - adds loading="lazy" + decoding="async" + fetchpriority="low" when missing
 * Keeps any existing attributes intact.
 */
export function enrichImages(html: string): string {
  return html.replace(/<img\b([^>]*?)\/?>/gi, (_m, attrs: string) => {
    let a = attrs;
    if (!/\bloading=/i.test(a)) a += ' loading="lazy"';
    if (!/\bdecoding=/i.test(a)) a += ' decoding="async"';
    if (!/\bfetchpriority=/i.test(a)) a += ' fetchpriority="low"';
    return `<img${a}>`;
  });
}

/**
 * Detects FAQ items in the article. Articles use the pattern:
 * <p><strong>1. Vprašanje?</strong><br/>Odgovor...</p>
 * placed after an H2 like "Pogosta vprašanja in odgovori".
 */
export function extractFaq(html: string): FaqItem[] {
  const faqMarker = /pogosta\s+vpra[sš]anja|faq/i;
  const h2Match = html.split(/<h2[^>]*>/i);
  if (h2Match.length < 2) return [];
  let faqBlock = "";
  for (let i = 1; i < h2Match.length; i++) {
    const segment = h2Match[i];
    const headingText = stripTags(segment.split("</h2>")[0] || "");
    if (faqMarker.test(headingText)) {
      faqBlock = segment.split("</h2>")[1] || "";
      break;
    }
  }
  if (!faqBlock) return [];
  const items: FaqItem[] = [];
  const paragraphs = faqBlock.match(/<p[^>]*>[\s\S]*?<\/p>/gi) ?? [];
  for (const p of paragraphs) {
    const inner = p.replace(/^<p[^>]*>|<\/p>$/gi, "");
    // Question lives in the first <strong>...</strong>, answer is the rest.
    const strongMatch = inner.match(/<strong[^>]*>([\s\S]*?)<\/strong>/i);
    if (!strongMatch) continue;
    const question = stripTags(strongMatch[1]).replace(/^\d+\.\s*/, "").trim();
    const afterStrong = inner.slice((strongMatch.index ?? 0) + strongMatch[0].length);
    const answer = stripTags(afterStrong.replace(/<br\s*\/?>/gi, " ")).trim();
    if (question && answer) items.push({ question, answer });
  }
  return items;
}
